import type { ReactNode } from 'react'
import { PARTIAL_TITLE, WITHHELD, WITHHELD_TITLE } from '../api/answer'

/**
 * THE NEW MODEL'S TWO STATES OF A FIGURE, drawn the same on every page (M8;
 * answer.ts). A blank figure there means withheld, and only that, so it says so
 * in words rather than a dash a reader takes for "nothing recorded". A figure
 * that leaves withheld values out keeps its value and gains a mark, because the
 * value is true for what the reader may see and wrong as the whole.
 */
export function Withheld() {
  return (
    <span className="text-(--color-ink-3)" title={WITHHELD_TITLE}>
      {WITHHELD}
    </span>
  )
}

/** A figure that leaves withheld values out: the value, and a mark that says so on hover. */
export function PartlyWithheld({ children }: { children: ReactNode }) {
  return (
    <span title={PARTIAL_TITLE}>
      {children}
      <sup className="ml-0.5 text-(--color-ink-3)" aria-label="partly withheld">
        *
      </sup>
    </span>
  )
}

/** Wraps a rendered figure in the mark when the row's figure leaves withheld values out. */
export const markPartial = (partial: boolean, node: ReactNode): ReactNode =>
  partial ? <PartlyWithheld>{node}</PartlyWithheld> : node

/**
 * A grouping row's name, as text (for an export). Rows whose name the reader may not
 * see fall into one blank member marked partly withheld (05 4.6, default 15): that
 * member is "Salesperson not shown, or none", not "(blank)", which reads as rows with
 * no salesperson at all (review R-28, 27 Oct 2026). "or none": rows the reader may see
 * that have no salesperson share that blank member (the server groups both under no
 * name). Any other blank stays "(blank)".
 */
export const memberText = (name: string | null | undefined, partial: boolean, level: string): string =>
  name || (partial ? `${level} not shown, or none` : '(blank)')

/** The same name drawn on a screen: quiet when blank, with the mark when partly withheld. */
export const memberName = (name: string | null | undefined, partial: boolean, level: string): ReactNode =>
  markPartial(partial, name || <span className="italic text-(--color-ink-4)">{memberText(name, partial, level)}</span>)
